<template>
  <Layout class="ma-0 container-presentational">
    <template #center-content>
      <div class="pa-6 pt-8 px-md-6 pt-md-0 mb-10">
        <TheAppLogo class="mb-2 mb-md-4 d-md-none" />
        <div class="d-flex align-baseline pt-md-6">
          <BaseHeader class="mb-4 neutral--text">
            <span>Cours disponibles</span>
          </BaseHeader>
          <v-spacer />
          <div class="d-md-none z-200">
            <font-awesome-icon
              icon="fa-light fa-sliders-up"
              class="brand-secondary--text"
              size="lg"
              @click="openFilterDialog = true"
            />
            <BaseDialog
              :value="openFilterDialog"
              @close="openFilterDialog = false"
              overlay-color="neutral tone-900"
              overlay-opacity="0.96"
              content-class="bottom-dialog"
            >
              <template #content>
                <TheCourseFilters
                  title="Filtres"
                  :filters-items="filtersItems"
                  filters-submit-button-text="Valider"
                  header-action-button-text="Supprimer tous les filtres"
                  @filter="
                    openFilterDialog = false;
                    recycleScrollerKey += 1;
                  "
                />
              </template>
            </BaseDialog>
          </div>
        </div>
        <RecycleScroller
          class="scroller"
          :items="courseItems"
          :key="recycleScrollerKey"
          :min-item-size="32"
          key-field="id"
          v-slot="{ item }"
          :emit-update="true"
        >
          <BaseCardWithIcon
            :title="item.title"
            :chip-text="availablePlaces(item)"
            @click.native="goToCourse(item)"
            :icon="`fa-light ${item.icon}`"
            class="mb-4 course-card"
          >
            <BaseIconWithText
              icon="fa-light fa-location-dot"
              :text="item.location"
              class="mb-4 mr-md-4 mb-sm-0"
            />
            <BaseIconWithText
              icon="fa-light fa-calendar"
              :text="item.day_and_time"
              class="mb-4 mr-md-4 mb-sm-0"
            />
            <BaseIconWithText
              icon="fa-light fa-user"
              :text="item.teacher"
              :class="{ 'mb-4 mb-sm-0 mr-md-4': item.handisport }"
            />
            <BaseIconWithText
              v-if="item.handisport"
              icon="fa-light fa-wheelchair"
              text="Compatible handisport"
            />
          </BaseCardWithIcon>
        </RecycleScroller>
      </div>
    </template>

    <template #right-content>
      <TheCourseFilters
        title="Filtres"
        :filters-items="filtersItems"
        filters-submit-button-text="Valider"
        header-action-button-text="Supprimer tous les filtres"
        class="mt-14"
        @filter="recycleScrollerKey += 1"
      />
    </template>
  </Layout>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { sortBy } from 'lodash';
import Layout from '@/views/containers/Layout.vue';
import BaseDialog from '@/components/BaseDialog.vue';
import BaseHeader from '@/components/BaseHeader.vue';
import BaseCardWithIcon from '@/components/BaseCardWithIcon.vue';
import BaseIconWithText from '@/components/BaseIconWithText.vue';
import TheCourseFilters from '@/views/containers/TheCourseFilters.vue';
import TheAppLogo from '@/views/containers/TheAppLogo.vue';

export default {
  name: 'Courses',

  components: {
    Layout,
    BaseDialog,
    BaseCardWithIcon,
    BaseHeader,
    BaseIconWithText,
    TheCourseFilters,
    TheAppLogo,
  },

  data() {
    return {
      recycleScrollerKey: 0,
      recycleItems: [],
      openFilterDialog: false,
      availabilities: [
        {
          id: '1',
          attributes: {
            name: this.$t('All'),
          },
        },
        {
          id: '2',
          attributes: {
            name: this.$t('With available spots'),
          },
        },
        {
          id: '3',
          attributes: {
            name: this.$t('Full'),
          },
        },
      ],
    };
  },

  computed: {
    ...mapGetters(['courseList']),
    ...mapState(['campuses', 'sports', 'filters']),

    sortedSports() {
      return sortBy(this.sports.data, 'attributes.name');
    },

    filtersItems() {
      return [
        {
          items: this.campuses.data,
          attribute: 'campus',
          label: 'Campus',
          placeholder: 'Sélectionner un campus',
          multiple: false,
        },
        {
          items: this.sortedSports,
          attribute: 'sport',
          label: 'Sujet',
          placeholder: 'Sélectionner des sujets',
          multiple: true,
        },
        {
          items: this.availabilities,
          attribute: 'availability',
          label: 'Disponibilité',
          placeholder: 'Sélectionner des disponibilités',
          multiple: false,
        },
      ];
    },

    courseItems() {
      const sortedCourses = sortBy(this.courseList, (course) => course.sport.attributes.name);
      return sortedCourses.map((course) => ({
        location: course.campuses.map((campus) => campus.attributes.name).join(', '),
        teacher: course.teacher.attributes.name,
        handisport: course.sport.attributes.handisport,
        day_and_time: course.attributes.day_and_time,
        remaining_spots: course.attributes.remaining_spots,
        id: parseInt(course.id, 10),
        title: course.sport.attributes.name,
        icon: course.sport.attributes.icon || 'fa-dumbbell',
      }));
    },
  },

  methods: {
    ...mapActions([
      'fetchCourses',
      'fetchCampuses',
      'fetchSports',
      'setCoursesFilter',
      'resetFilters',
    ]),

    updateData() {
      this.recycleItems = [];
      this.recycleItems = this.courseItems;
    },

    setFilters(selectedFilters) {
      this.setCampusFilter(selectedFilters.Campus);
      this.setSportFilter(selectedFilters.Sport);
      this.setAvailabilityFilter(selectedFilters['Disponibilités']);
    },

    availablePlaces(courseItem) {
      if (courseItem.remaining_spots > 0) {
        return this.$i18n.tc('RemainingSpots', courseItem.remaining_spots);
      }
      return '0 place restante';
    },

    setCampusFilter(campusId) {
      this.setFilter('campus', campusId);
    },
    setSportFilter(sportId) {
      this.setFilter('sport', sportId);
    },
    setFilter(attribute, value) {
      this.setCoursesFilter({ filter: attribute, value });
    },
    setAvailabilityFilter(availabilityId) {
      this.setFilter('availability', availabilityId);
    },
    goToCourse(course) {
      if (!course.disabled) {
        this.$router.push(`/courses/${course.id}`);
      }
    },
  },

  created() {
    this.fetchCourses();
    this.fetchCampuses();
    this.fetchSports();
  },
};
</script>

<style lang="scss">
.vue-recycle-scroller__item-view {
  position: relative !important;
  transform: none !important;
}
</style>
